import type { Going, RaceType, RichContentRaceDetails } from '@ps/isp-web-services-types';
import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import FormattedDateTime from '../FormattedDateTime/FormattedDateTime';
import { HorseRaceDistance } from '../HorseRaceDistance/HorseRaceDistance';
import RaceStatus from '../RaceStatus/RaceStatus';

import {
  TXTWCL_Bumper,
  TXTWCL_Chase,
  TXTWCL_Class_N0,
  TXTWCL_Dead_2,
  TXTWCL_Fast_3,
  TXTWCL_Firm,
  TXTWCL_Flat,
  TXTWCL_Frozen,
  TXTWCL_Good_4,
  TXTWCL_Good_Firm,
  TXTWCL_Good_Soft,
  TXTWCL_Hard,
  TXTWCL_Heavy,
  TXTWCL_Hurdle,
  TXTWCL_Muddy,
  TXTWCL_Sloppy,
  TXTWCL_Slow,
  TXTWCL_Soft_1,
  TXTWCL_Standard_2,
  TXTWCL_Standard_Fast,
  TXTWCL_Standard_Slow,
  TXTWCL_Wet_Fast,
  TXTWCL_Yielding
} from './RaceDetailsNextRaces.messages.json';
import styles from './RaceDetailsNextRaces.scss';

interface Props {
  details?: Pick<RichContentRaceDetails, 'distance' | 'type' | 'going' | 'raceClass' | 'status'>;
  startTime?: string;
}

const goingMap: Partial<Record<Going, TranslationTag>> = {
  DEAD: TXTWCL_Dead_2,
  FAST: TXTWCL_Fast_3,
  FIRM: TXTWCL_Firm,
  FROZEN: TXTWCL_Frozen,
  GOOD: TXTWCL_Good_4,
  GOOD_FIRM: TXTWCL_Good_Firm,
  GOOD_SOFT: TXTWCL_Good_Soft,
  HARD: TXTWCL_Hard,
  HEAVY: TXTWCL_Heavy,
  MUDDY: TXTWCL_Muddy,
  SLOPPY: TXTWCL_Sloppy,
  SLOW: TXTWCL_Slow,
  SOFT: TXTWCL_Soft_1,
  STANDARD: TXTWCL_Standard_2,
  STD: TXTWCL_Standard_2,
  STD_FAST: TXTWCL_Standard_Fast,
  STD_SLOW: TXTWCL_Standard_Slow,
  WET_FAST: TXTWCL_Wet_Fast,
  YIELDING: TXTWCL_Yielding
};

const typeMap: Record<RaceType, TranslationTag> = {
  BUMPER: TXTWCL_Bumper,
  CHASE: TXTWCL_Chase,
  FLAT: TXTWCL_Flat,
  HURDLE: TXTWCL_Hurdle
};

const RaceDetailsNextRaces: React.FC<Props> = ({ details, startTime }) => {
  const { type, distance, going, raceClass, status } = details || {};
  const goingTranslation = going && goingMap[going];
  const typeTranslation = type && typeMap[type];
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shouldShowRaceStatus = status && status !== 'DORMANT';
  const STATUS_ANIMATION_INTERVAL = 2500;

  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!shouldShowRaceStatus) return;

    const interval = setInterval(() => {
      setIsActive((prev) => !prev);
    }, STATUS_ANIMATION_INTERVAL);

    return () => clearInterval(interval);
  }, [shouldShowRaceStatus]);

  return (
    <div data-testid="race-details-card" ref={containerRef} className={styles.container}>
      {isActive ? (
        <span className={classNames(styles.details, shouldShowRaceStatus && styles.slide)}>
          {startTime ? (
            <FormattedDateTime date={startTime} format="long" weekday="long" hideTime />
          ) : null}
          {typeTranslation ? (
            <span>
              <Translation {...typeTranslation} />
            </span>
          ) : null}
          {raceClass ? (
            <span>
              <Translation values={{ 0: raceClass }} {...TXTWCL_Class_N0} />
            </span>
          ) : null}
          {distance ? <HorseRaceDistance distance={distance} /> : null}
          {goingTranslation ? (
            <span>
              <Translation {...goingTranslation} />
            </span>
          ) : null}
        </span>
      ) : (
        shouldShowRaceStatus && (
          <span className={classNames(styles.inPlay, styles.slide)}>
            <RaceStatus status={status} isTextOnly />
          </span>
        )
      )}
    </div>
  );
};

export default RaceDetailsNextRaces;
